import { DEFAULT_HEADERS, REQUEST_PATHS } from '@common/constants/request.constants'
import { LocalStorageKey } from '@common/enums/global.enums'
import { AuthLink, HttpStatusCode, TypeRequest } from '@common/enums/request.enums'

/**
 * Api error
 */
class ApiError<T> extends Error {
	code: number
	previous: T

	constructor(message: string, code: number, previous: T) {
		super(message)
		this.code = code
		this.previous = previous
	}
}

/**
 * Handle response status
 * @param response
 * @param redirectOnForbidden
 * @param redirectOnNotFound
 */
const handleResponseStatus = (response: Response, redirectOnForbidden = true, redirectOnNotFound = true) => {
	if (response.status === HttpStatusCode.UNAUTHORIZED) {
		window.location.href = AuthLink.LOG_IN + `?redirect_uri=${window.location.pathname}`
	}
	if ((redirectOnForbidden && response.status === HttpStatusCode.FORBIDDEN) || (redirectOnNotFound && response.status === HttpStatusCode.NOT_FOUND)) {
		window.location.pathname = '/not-found'
	}
}

/**
 * Request with body
 * @param config
 * @param typeRequest
 * @param path
 * @param contentBody
 * @param redirectOnForbidden
 */
export const requestWithBody = async <T>(config: RequestInit = {}, typeRequest: TypeRequest, path: string, contentBody: string | FormData, redirectOnForbidden = true): Promise<T> => {
	return fetchData(path, {
		body: contentBody,
		method: typeRequest,
		...config
	}, REQUEST_PATHS.BASE_PATH, redirectOnForbidden)
}

/**
 * Fetch data
 * @param path
 * @param newConfig
 * @param basePath
 * @param redirectOnForbidden
 */
export const fetchData = async <T>(path: string, newConfig: RequestInit, basePath = REQUEST_PATHS.BASE_PATH, redirectOnForbidden = true) => {
	const url = `${basePath}${path}`
	const token = localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)
	const config: RequestInit = {
		...newConfig,
		headers: {
			'Accept-Language': localStorage.getItem(LocalStorageKey.SELECTED_LANGUAGE) ?? 'en',
			...newConfig.headers,
			...(token && { Authorizationjwt: `Bearer ${token}` })
		}
	}

	return fetch(url, config)
		.then(async (response) => {
			if (response.status === HttpStatusCode.NO_CONTENT) {
				return {} as T
			}
			handleResponseStatus(response, redirectOnForbidden)
			return response.json()
				.then((data) => {
					if (response.ok) {
						return data as T
					} else {
						const message = (data.message && data.message !== '') ? `global.errors.${data.message}` : 'global.errors.occured'
						const extraString = data.params ? ' ' + data.params.toString() : ''
						throw new ApiError<T>(message + extraString, response.status, data)
					}
				})
				.catch((e) => {
					if (e instanceof ApiError) throw e
					throw new ApiError<Error>('global.errors.occured', response.status, e)
				})
		})
}

/**
 * Get
 * @param path
 * @param config
 * @param basePath
 * @param redirectOnForbidden
 */
export const get = async <T>(path: string, config: RequestInit = {}, basePath = REQUEST_PATHS.BASE_PATH, redirectOnForbidden = true): Promise<T> => {
	const { headers, ...others } = config

	const newConfig = {
		method: TypeRequest.GET,
		headers: {
			...DEFAULT_HEADERS,
			...headers
		},
		...others
	}
	return fetchData(path, newConfig, basePath, redirectOnForbidden)
}

/**
 * Post
 * @param path
 * @param contentBody
 * @param config
 * @param redirectOnForbidden
 */
export const post = async <T>(path: string, contentBody: string | FormData, config: RequestInit = {}, redirectOnForbidden = true): Promise<T> => {
	return requestWithBody(config, TypeRequest.POST, path, contentBody, redirectOnForbidden)
}

/**
 * Put
 * @param path
 * @param contentBody
 * @param config
 * @param redirectOnForbidden
 */
export const put = async <T>(path: string, contentBody: string | FormData, config: RequestInit = {}, redirectOnForbidden = true): Promise<T> => {
	return requestWithBody(config, TypeRequest.PUT, path, contentBody, redirectOnForbidden)
}

/**
 * Patch
 * @param path
 * @param contentBody
 * @param config
 * @param redirectOnForbidden
 */
export const patch = async <T>(path: string, contentBody: string | FormData, config: RequestInit = {}, redirectOnForbidden = true): Promise<T> => {
	return requestWithBody(config, TypeRequest.PATCH, path, contentBody, redirectOnForbidden)
}

/**
 * Delete request
 * @param path
 * @param config
 */
export const deleteRequest = async (path: string, config: RequestInit = {}): Promise<void> => {
	return fetchData(path, {
		method: TypeRequest.DELETE,
		...config
	})
}

/**
 * Auth
 * @param path
 */
export const auth = async <T>(path: string): Promise<T> => {
	const newConfig = {
		method: TypeRequest.GET
	}
	return fetchData(path, newConfig, REQUEST_PATHS.AUTH)
}

/**
 * Fetch blob
 * @param path
 * @param newConfig
 */
export const fetchBlob = async (path: string, newConfig: RequestInit) => {
	const config = {
		...newConfig,
		method: TypeRequest.GET,
		responseType: 'blob',
		headers: {
			Authorizationjwt: `Bearer ${localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)}`,
			...newConfig.headers
		}
	}
	const url = `${REQUEST_PATHS.BASE_PATH}${path}`
	return fetch(url, config)
		.then((response: Response) => {
			return response.blob()
		})
}

/**
 * Fetch file
 * @param path
 * @param config
 * @param typeRequest
 */
export const fetchFile = async (path: string, config: RequestInit = {}, typeRequest: TypeRequest = TypeRequest.GET) => {
	const newConfig = {
		...config,
		headers: {
			'Accept-Language': localStorage.getItem(LocalStorageKey.SELECTED_LANGUAGE) ?? 'en',
			Authorizationjwt: `Bearer ${localStorage.getItem(LocalStorageKey.ACCESS_TOKEN)}`,
			...config?.headers
		},
		method: typeRequest,
		responseType: 'blob'
	}
	const url = `${REQUEST_PATHS.BASE_PATH}${path}`
	return fetch(url, newConfig)
		.then((response: Response) => {
			handleResponseStatus(response)
			const header = response.headers.get('Content-Disposition')
			if (!header) {
				throw Error('No content disposition header')
			}
			const parts = header!.split(';')
			const filename = parts[1].split('=')[1].split('\'').join('')
			return Promise.all([response.blob(), filename])
		})
		.then(([blob, filename]) => {
			if (blob != null) {
				const url = window.URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = filename
				document.body.appendChild(a)
				a.click()
				a.remove()
			}
		})
}
